import type { NextApiHandler } from 'next'
import { getOpCoFromRequest } from '@reward-platform/platform-config/server'
import { Auth0B2CException, handleAuth0ErrorStatus, logAuth0Error } from '../error'
import { serializeSilentLoginCookie } from '../../utils'
import { validateUserToken } from '../validateAccessToken'
import { getAuth0Server } from '../auth0/auth0.instance'

type ApiAuthHeaderConfig = {
  required?: boolean
}

export const withApiAuthorizationHeader =
  (apiRoute: NextApiHandler, { required = true }: ApiAuthHeaderConfig = {}): NextApiHandler =>
  async (req, res) => {
    // If the Authorization header is already set (e.g. by Avios Auth), skip Auth0 entirely.
    if (!req.headers.authorization) {
      try {
        const auth0 = getAuth0Server(req)
        const { token } = await auth0.getAccessToken(req, res)

        const opCo = getOpCoFromRequest(req)
        const validatedToken = await validateUserToken(opCo, token)

        // Temporarily set the authorization header for BFF API calls
        req.headers.authorization = `Bearer ${validatedToken}`
      } catch (error) {
        // if access token fails when auth header is required
        if (required && error instanceof Error) {
          // will trigger a silent login when user navigates away or refreshes
          const resetSilentLoginCookie = serializeSilentLoginCookie(false)
          res.setHeader('Set-Cookie', resetSilentLoginCookie)

          const authError = new Auth0B2CException(error, 'REQUEST_UNAUTHORIZED')
          const message = 'Failed to get access token header'
          logAuth0Error(message, authError, req, res)
          handleAuth0ErrorStatus(authError, req, res)
          return undefined
        }
      }
    }

    return apiRoute(req, res)
  }
